/**
 * Image handling utilities for canvas templates
 * Extracted from AddCanvasTemplate.js to reduce file size
 */
import { compressImage } from "./imageUploadUtils";
import { openCanvasDB } from "../../../util/indexedDBUtils";

/**
 * Save image data directly to IndexedDB in MyCanvas format
 * @param {Object} imageData - Image data to save
 * @param {string|null} pageId - Page identifier (defaults to "currentCanvas")
 * @param {Function} getCurrentSnapshot - Function to get current snapshot
 * @returns {Promise<void>}
 */
export const saveImageToIndexedDB = async (
  imageData,
  pageId = null,
  getCurrentSnapshot = () => null
) => {
  // Create a unique key for the image based on timestamp
  const timestamp = new Date().getTime();
  const uniqueId = `img_${timestamp}`;

  // Override the image ID with our unique ID
  const imageWithUniqueId = {
    ...imageData,
    id: uniqueId,
  };

  // Open IndexedDB and save the image
  const db = await openCanvasDB();
  if (!db.objectStoreNames.contains("canvasElements")) return;

  // First retrieve existing data
  const transaction = db.transaction(["canvasElements"], "readwrite");
  const store = transaction.objectStore("canvasElements");
  const key = pageId || "currentCanvas";
  const getRequest = store.get(key);

  getRequest.onsuccess = (event) => {
    let data = event.target.result || {
      id: key,
      texts: [],
      rectangles: [],
      circles: [],
      arrows: [],
      scribbles: [],
      images: [],
      lastModified: new Date().toISOString(),
    };

    // Check if image with same source already exists
    const imageExists = data.images.some(
      (img) =>
        img.src === imageWithUniqueId.src &&
        Math.abs(img.x - imageWithUniqueId.x) < 5 &&
        Math.abs(img.y - imageWithUniqueId.y) < 5
    );

    if (!imageExists) {
      // Add the new image to the images array only if it doesn't exist
      data.images.push({
        ...imageWithUniqueId,
        id: imageWithUniqueId.id,
        x: imageWithUniqueId.x,
        y: imageWithUniqueId.y,
        width: imageWithUniqueId.width,
        height: imageWithUniqueId.height,
        src: imageWithUniqueId.src,
      });

      // Update the timestamp
      data.lastModified = new Date().toISOString();

      // Save the updated data back to IndexedDB
      const putRequest = store.put(data);

      putRequest.onsuccess = () => {
        // Also update flags for MyCanvas to detect changes
        const currentSnapshot = getCurrentSnapshot();
        if (currentSnapshot) {
          localStorage.setItem("canvasSnapshot", currentSnapshot);
        }
        sessionStorage.setItem(
          "canvasStateInfo",
          JSON.stringify({
            hasChanges: true,
            lastUpdate: new Date().toISOString(),
          })
        );
      };

      putRequest.onerror = (error) => {
        // Silently fail
      };
    }
  };

  getRequest.onerror = (error) => {
    // Silently fail
  };
};

/**
 * Add image to canvas template
 * @param {string} imageSrc - Image source (base64 or URL)
 * @param {string|null} pageId - Page identifier
 * @param {Object} callbacks - Callback functions for state updates
 * @param {Function} callbacks.setCanvasElements - Set canvas elements state
 * @param {Function} callbacks.setCanvasPages - Set canvas pages state
 * @param {Function} callbacks.updateCanvasPage - Update specific canvas page
 * @param {Function} callbacks.showFeedback - Show feedback message
 * @param {Function} callbacks.getCanvasPages - Get current canvas pages
 * @param {Object} callbacks.template - Template object for page reference
 * @returns {Promise<void>}
 */
export const addImageToTemplate = async (
  imageSrc,
  pageId = null,
  callbacks = {}
) => {
  const {
    setCanvasElements,
    setCanvasPages,
    updateCanvasPage,
    showFeedback,
    getCanvasPages = () => [],
    template,
  } = callbacks;

  // Compress the image before adding to canvas
  const compressedImageSrc = await compressImage(imageSrc);

  // Instead of navigating to full canvas, create an image and add it to the preview
  const img = new window.Image();
  img.src = compressedImageSrc;

  return new Promise((resolve, reject) => {
    img.onload = () => {
      // Generate a unique ID for the image
      const imageId = Date.now().toString();

      // Add the image to local canvas elements
      const newImage = {
        id: imageId,
        src: compressedImageSrc,
        x: 50,
        y: 50,
        width: 100,
        height: 100,
      };

      const canvasPages = getCanvasPages();

      // Save to IndexedDB for MyCanvas compatibility
      // This is crucial to ensure MyCanvas can load our images
      saveImageToIndexedDB(newImage, pageId, () => {
        return canvasPages[0]?.snapshot || null;
      });

      // Update our local state
      if (setCanvasElements) {
        setCanvasElements((prev) => {
          const updatedElements = {
            ...prev,
            images: [...(prev.images || []), newImage],
          };

          // Also save to localStorage for persistence between pages
          try {
            localStorage.setItem(
              "canvasElements",
              JSON.stringify(updatedElements)
            );
          } catch (e) {
            // Silently fail
          }

          return updatedElements;
        });
      }

      // Also update the corresponding page's elements so navigation uses fresh data
      if (setCanvasPages) {
        setCanvasPages((prevPages) => {
          if (!prevPages || prevPages.length === 0) return prevPages;
          const targetPageId =
            pageId || prevPages[0]?.id || template?.pages?.[0]?.canvas_page_ref;
          const pageIndex = prevPages.findIndex((p) => p.id === targetPageId);
          const idx = pageIndex !== -1 ? pageIndex : 0;
          const updatedPages = [...prevPages];
          const oldPage = updatedPages[idx];
          const updatedElements = {
            ...(oldPage.elements || {}),
            images: [...(oldPage.elements?.images || []), newImage],
          };
          updatedPages[idx] = { ...oldPage, elements: updatedElements };
          return updatedPages;
        });
      }

      // Update snapshot to include the new image
      // Create a temporary canvas to draw the image on the existing snapshot
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // If we have an existing snapshot, use it as the base
      const currentSnapshot = canvasPages[0]?.snapshot;
      if (currentSnapshot) {
        const baseImage = new Image();
        baseImage.onload = () => {
          canvas.width = baseImage.width;
          canvas.height = baseImage.height;
          ctx.drawImage(baseImage, 0, 0);
          ctx.drawImage(img, 50, 50, 100, 100);

          // Update the snapshot
          const newSnapshot = canvas.toDataURL();
          localStorage.setItem("canvasSnapshot", newSnapshot);

          // Update canvas page with new snapshot
          if (updateCanvasPage) {
            updateCanvasPage(0, { snapshot: newSnapshot });
          }

          // Set unsaved changes flag
          sessionStorage.setItem(
            "canvasStateInfo",
            JSON.stringify({
              hasChanges: true,
              lastUpdate: new Date().toISOString(),
            })
          );

          if (showFeedback) {
            showFeedback("Image added successfully!", "success");
          }
          resolve();
        };
        baseImage.onerror = () =>
          reject(new Error("Failed to load base image"));
        baseImage.src = currentSnapshot;
      } else {
        // Create a new snapshot with just the image
        canvas.width = 800;
        canvas.height = 600;
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 50, 50, 100, 100);

        const newSnapshot = canvas.toDataURL();
        localStorage.setItem("canvasSnapshot", newSnapshot);
        if (updateCanvasPage) {
          updateCanvasPage(0, { snapshot: newSnapshot });
        }

        sessionStorage.setItem(
          "canvasStateInfo",
          JSON.stringify({
            hasChanges: true,
            lastUpdate: new Date().toISOString(),
          })
        );

        if (showFeedback) {
          showFeedback("Image added successfully!", "success");
        }
        resolve();
      }
    };

    img.onerror = () => {
      if (showFeedback) {
        showFeedback("Failed to load image", "error");
      }
      reject(new Error("Failed to load image"));
    };
  });
};
